import type { IAgentTool } from '../../../../core/ai/agent/IAgentTool';
import { normalizeAgentColor } from '../../../../core/ai/agent/tools/colorMatchTool';
import { ChangeNodeStyleCommand } from '../../../../core/commands/ChangeNodeStyleCommand';
import type { Node } from '../../../../core/models/scene';

const TARGET_SUFFIX = /\s+(?:shapes?|steps?|nodes?)$/i;

function normalizeTarget(value: string): string {
  return value
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(TARGET_SUFFIX, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function readStrings(record: Record<string, unknown>, keys: readonly string[]): string[] {
  return keys.flatMap((key) => typeof record[key] === 'string' ? [record[key] as string] : []);
}

function nodeMatchesTarget(node: Node, rawTarget: string): boolean {
  const target = normalizeTarget(rawTarget);
  if (!target) return false;

  const attrs = readRecord(node.attrs);
  const props = readRecord(node.props);
  const labels = [
    ...readStrings(node as unknown as Record<string, unknown>, ['name', 'text', 'label']),
    ...readStrings(attrs, ['label', 'name', 'text']),
    ...readStrings(props, ['label', 'name', 'text']),
  ].map(normalizeTarget);
  if (labels.includes(target)) return true;

  const typeTokens = [
    node.type,
    ...readStrings(attrs, ['variant', 'shapeType', 'stepKind', 'processNodeType']),
    ...readStrings(props, ['variant', 'shapeType', 'stepKind', 'processNodeType']),
  ].map(normalizeTarget);

  if (target === 'decision' || target === 'gateway') {
    return typeTokens.some((token) => token === 'decision' || token.includes('gateway'));
  }
  if (target === 'task' || target === 'process') {
    return typeTokens.some((token) => token === 'task' || token === 'process');
  }
  return typeTokens.includes(target);
}

export function resolveProcessColorTargetIds(
  nodes: Node[],
  selectedNodeIds: string[],
  params: Record<string, unknown>,
): { targetIds: string[]; targetDescription: string } {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const rawTarget = String(params.target ?? params.shapeType ?? params.shapeName ?? '').trim();
  if (rawTarget) {
    return {
      targetIds: nodes.filter((node) => nodeMatchesTarget(node, rawTarget)).map((node) => node.id),
      targetDescription: `matching “${rawTarget}”`,
    };
  }

  const scope = params.scope === 'all' ? 'all' : 'selected';
  return {
    targetIds: scope === 'all'
      ? Array.from(nodeIds)
      : selectedNodeIds.filter((id) => nodeIds.has(id)),
    targetDescription: scope,
  };
}

export const setShapeColorTool: IAgentTool = {
  name: 'set_shape_color',
  description:
    'Change the fill color of process-map shapes. Target one visible shape by label, a shape type such as decision, selected shapes, or every shape. A supplied target always limits the edit even if scope is all.',
  parameters: {
    type: 'object',
    properties: {
      color: { type: 'string', description: 'Named color or hex color, for example blue or #3b82f6' },
      scope: {
        type: 'string',
        enum: ['selected', 'all'],
        description: 'Color the selected shapes (default) or all shapes in the process map.',
      },
      target: {
        type: 'string',
        description: 'Optional exact visible shape label or process shape type to color, for example “Review request” or “decision”.',
      },
    },
    required: ['color'],
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'visual',
  category: 'edit',

  async execute(params, context) {
    if (!context.canWrite) {
      return { success: false, summary: 'You do not have edit access to this diagram.' };
    }

    const color = normalizeAgentColor(String(params.color ?? params.fillColor ?? ''));
    if (!color) {
      return { success: false, summary: 'Choose a named color or a valid hex color.' };
    }

    const { targetIds, targetDescription } = resolveProcessColorTargetIds(
      context.diagramData.nodes,
      context.selectedNodeIds,
      params,
    );

    if (targetIds.length === 0) {
      return {
        success: false,
        summary: params.target
          ? `No process shapes match “${String(params.target)}”.`
          : params.scope === 'all'
            ? 'There are no process shapes to color.'
            : 'Select one or more process shapes to color.',
      };
    }

    return {
      success: true,
      summary: `Colored ${targetIds.length} ${targetDescription === 'selected' ? 'selected ' : ''}shape${targetIds.length === 1 ? '' : 's'} ${color}${targetDescription.startsWith('matching') ? ` ${targetDescription}` : ''}.`,
      execution: {
        kind: 'command',
        command: new ChangeNodeStyleCommand(targetIds, 'fillColor', color),
      },
    };
  },
};
